import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Copy, ExternalLink, Eye, EyeOff, GripVertical,
  Loader2, Monitor, Paintbrush, Plus, RotateCcw, Save, Smartphone, Star, Trash2, X, Lock, Type, Video, SquareMousePointer,
} from 'lucide-react';
import { mergeAll, FIELD_DEFS, SECTION_META, BLOCK_LIBRARY, newBlock, newExtra, EXTRA_ANCHORS } from './content';
import { getDeep, setDeep } from './EditableBits';
import { wistiaEmbed } from '../ThreeJobs';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PAGE_TABS = [
  { id: 'lander', name: 'Lander', path: '/challenge' },
  { id: 'kit', name: 'Kit ($27)', path: '/challenge/kit?reg=preview' },
  { id: 'thanks', name: 'Thanks', path: '/challenge/thanks' },
  { id: 'vip', name: 'VIP', path: '/challenge/vip' },
];
// '' (default) renders at the element's designed size = L
const SIZES = [['xs', 'XS'], ['sm', 'S'], ['md', 'M'], ['lg', 'L'], ['xl', 'XL'], ['xxl', 'XXL']];
const WIDTHS = [['', 'Width'], ['narrow', 'Narrow'], ['medium', 'Medium'], ['wide', 'Wide'], ['full', 'Full']];
const FX = [['arrow', 'Arrow slide (hover)'], ['nudge', 'Side-to-side nudge'], ['pulse', 'Pulse'], ['none', 'None']];
const EXTRA_TYPES = [['text', 'Text', Type], ['button', 'Button', SquareMousePointer], ['video', 'Video', Video]];
const CELL_KINDS = [['text', 'Text'], ['check', '✓ Check'], ['x', '✕ Cross'], ['dash', '— Dash'], ['button', 'Button']];
const COL_WIDTHS = [['', 'Normal'], ['narrow', 'Narrow'], ['wide', 'Wide']];

const inp = 'w-full px-3 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-white placeholder-white/25 focus:outline-none focus:border-cyan-400/60';
const miniSel = 'px-2 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-xs text-white/70 focus:outline-none focus:border-cyan-400/60';
const fieldBox = (active) => `rounded-xl border p-3 space-y-2 transition-colors ${active ? 'border-cyan-400/80 bg-cyan-400/[0.06]' : 'border-white/10 bg-white/[0.03]'}`;
const lbl = 'block text-[11px] font-bold uppercase tracking-wider text-white/50';
const snippet = (t) => {
  const s = String(t || '').replace(/\*\*|__|\{color:[^}]+\}|\{\/color\}/g, '').replace(/\*/g, '').replace(/\n/g, ' · ');
  return s.length > 64 ? `${s.slice(0, 64)}…` : s || '(empty)';
};

const ColorDot = ({ value, onChange, testid, title = 'Color' }) => (
  <span className="inline-flex items-center gap-1">
    <label className="relative w-7 h-7 rounded-lg border border-white/20 overflow-hidden cursor-pointer shrink-0" title={title} style={{ background: value || 'rgba(255,255,255,0.05)' }}>
      {!value && <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white/40">A</span>}
      <input type="color" value={value || '#141414'} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid={testid} />
    </label>
    {value && <button onClick={() => onChange(null)} className="text-white/30 hover:text-white/70" title="Reset color"><X size={12} /></button>}
  </span>
);

const MiniToggle = ({ on, onToggle, testid }) => (
  <button onClick={onToggle} className={`w-10 h-6 rounded-full p-0.5 transition-colors shrink-0 ${on ? 'bg-cyan-400' : 'bg-white/15'}`} data-testid={testid}>
    <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : ''}`} />
  </button>
);

/* Shared style row for any text value: size / bold / underline / color / width */
const StyleRow = ({ v = {}, onChange, k }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <select value={v.size || 'lg'} onChange={(e) => onChange({ size: e.target.value === 'lg' ? null : e.target.value })} className={miniSel} title="Font size" data-testid={`editor-size-${k}`}>
      {SIZES.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
    </select>
    <button
      onClick={() => onChange({ bold: !v.bold })}
      className={`w-8 h-8 rounded-lg border text-xs font-black transition-colors ${v.bold ? 'bg-cyan-400 text-black border-cyan-400' : 'border-white/10 text-white/60 hover:text-white'}`}
      title="Bold the whole element"
      data-testid={`editor-bold-${k}`}
    >B</button>
    <button
      onClick={() => onChange({ underline: !v.underline })}
      className={`w-8 h-8 rounded-lg border text-xs font-bold underline underline-offset-2 transition-colors ${v.underline ? 'bg-cyan-400 text-black border-cyan-400' : 'border-white/10 text-white/60 hover:text-white'}`}
      title="Underline the whole element"
      data-testid={`editor-underline-${k}`}
    >U</button>
    <ColorDot value={v.color} onChange={(c) => onChange({ color: c })} testid={`editor-color-${k}`} title="Text color" />
    <select value={v.width || ''} onChange={(e) => onChange({ width: e.target.value || null })} className={miniSel} title="Copy width" data-testid={`editor-width-${k}`}>
      {WIDTHS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
    </select>
  </div>
);

/* Button click action: default / link / funnel page / scroll to section */
const CtaActionRow = ({ v = {}, onChange, sections = [], k = '' }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] text-white/45">Click action</span>
      <select value={v.action || ''} onChange={(e) => onChange({ action: e.target.value || null })} className={miniSel} data-testid={`editor-cta-action-${k}`}>
        <option value="">Default (register / original)</option>
        <option value="url">Open a link</option>
        <option value="page">Go to a funnel page</option>
        <option value="section">Scroll to a section</option>
      </select>
    </div>
    {v.action === 'url' && (
      <>
        <input value={v.href || ''} onChange={(e) => onChange({ href: e.target.value })} className={inp} placeholder="https://…" data-testid={`editor-cta-href-${k}`} />
        <label className="flex items-center gap-2 text-[11px] text-white/45 cursor-pointer">
          <input type="checkbox" checked={!!v.new_tab} onChange={(e) => onChange({ new_tab: e.target.checked })} data-testid={`editor-cta-newtab-${k}`} /> Open in a new tab
        </label>
      </>
    )}
    {v.action === 'page' && (
      <select value={v.page || ''} onChange={(e) => onChange({ page: e.target.value })} className={`${miniSel} w-full`} data-testid={`editor-cta-page-${k}`}>
        <option value="">Choose a page…</option>
        {PAGE_TABS.map((t) => <option key={t.id} value={t.path.split('?')[0]}>{t.name}</option>)}
      </select>
    )}
    {v.action === 'section' && (
      <select value={v.to_sec || ''} onChange={(e) => onChange({ to_sec: e.target.value })} className={`${miniSel} w-full`} data-testid={`editor-cta-tosec-${k}`}>
        <option value="">Choose a section…</option>
        {sections.map((os) => <option key={os.id} value={os.id}>{(SECTION_META[os.type] || {}).name || os.type}</option>)}
      </select>
    )}
  </div>
);

const TextControl = ({ def, v = {}, onChange, active, onJump, onDuplicate }) => (
  <div id={`fld-${def.k}`} className={fieldBox(active)} data-testid={`editor-field-${def.k}`}>
    <div className="flex items-start justify-between gap-2">
      <button onClick={onJump} className={`text-left flex-1 min-w-0 group ${v.hidden ? 'opacity-40' : ''}`} data-testid={`editor-jump-${def.k}`}>
        <span className={lbl}>{def.label}{v.hidden ? ' · hidden' : ''}</span>
        <span className="block text-[11px] text-white/40 group-hover:text-white/60 leading-snug truncate transition-colors">{snippet(v.t)}</span>
      </button>
      <button onClick={() => onChange({ hidden: !v.hidden })} className="p-1 rounded text-white/35 hover:text-white transition-colors shrink-0" title={v.hidden ? 'Show this element again' : 'Delete this element from the page (you can bring it back here)'} data-testid={`editor-hide-${def.k}`}>
        {v.hidden ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
      {onDuplicate && (
        <button onClick={onDuplicate} className="p-1 rounded text-white/35 hover:text-cyan-400 transition-colors shrink-0" title="Duplicate this text as a new element" data-testid={`editor-dup-${def.k}`}>
          <Copy size={12} />
        </button>
      )}
    </div>
    {!v.hidden && <StyleRow v={v} onChange={onChange} k={def.k} />}
  </div>
);

const CtaControl = ({ def, v = {}, onChange, active, onJump, sections }) => (
  <div id={`fld-${def.k}`} className={fieldBox(active)} data-testid={`editor-field-${def.k}`}>
    <div className="flex items-start justify-between gap-2">
      <button onClick={onJump} className={`text-left flex-1 min-w-0 group ${v.hidden ? 'opacity-40' : ''}`} data-testid={`editor-jump-${def.k}`}>
        <span className={lbl}>{def.label}{v.hidden ? ' · hidden' : ''}</span>
        <span className="block text-[11px] text-white/40 group-hover:text-white/60 leading-snug truncate transition-colors">{snippet(v.label)}</span>
      </button>
      <button onClick={() => onChange({ hidden: !v.hidden })} className="p-1 rounded text-white/35 hover:text-white transition-colors shrink-0" title={v.hidden ? 'Show this button again' : 'Delete this button from the page (you can bring it back here)'} data-testid={`editor-hide-${def.k}`}>
        {v.hidden ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
    </div>
    {!v.hidden && (
      <>
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Button <ColorDot value={v.bg} onChange={(c) => onChange({ bg: c })} testid={`editor-cta-bg-${def.k}`} title="Button color" /></span>
          <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Text <ColorDot value={v.fg} onChange={(c) => onChange({ fg: c })} testid={`editor-cta-fg-${def.k}`} title="Button text color" /></span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-white/45">Attention effect</span>
          <select value={v.fx || 'arrow'} onChange={(e) => onChange({ fx: e.target.value })} className={miniSel} data-testid={`editor-cta-fx-${def.k}`}>
            {FX.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
          </select>
        </div>
        <CtaActionRow v={v} onChange={onChange} sections={sections} k={def.k} />
      </>
    )}
  </div>
);

const ColorControl = ({ def, v, onChange, active }) => (
  <div id={`fld-${def.k}`} className={`${fieldBox(active)} flex items-center justify-between`} data-testid={`editor-field-${def.k}`}>
    <label className={lbl}>{def.label}</label>
    <ColorDot value={v} onChange={onChange} testid={`editor-color-input-${def.k}`} title={def.label} />
  </div>
);

const ToggleControl = ({ def, v, onChange }) => {
  const on = def.defaultOff ? !!v : v !== false;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center justify-between" data-testid={`editor-field-${def.k}`}>
      <label className="text-xs font-semibold text-white/70">{def.label}</label>
      <MiniToggle on={on} onToggle={() => onChange(!on)} testid={`editor-toggle-${def.k}`} />
    </div>
  );
};

const isFileVid = (u) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u || '');

const VideoInput = ({ value, onChange, testid, allowFile }) => {
  const embed = wistiaEmbed(value || '');
  const file = allowFile && !embed && isFileVid(value);
  return (
    <>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className={inp} placeholder={allowFile ? 'Wistia link or direct .mp4 URL' : 'Paste the Wistia embed code, video link, or media ID'} data-testid={testid} />
      {value && (embed ? (
        <div className="rounded-lg overflow-hidden aspect-video bg-black">
          <iframe src={embed} title="preview" allow="autoplay; fullscreen" allowFullScreen className="w-full h-full" />
        </div>
      ) : file ? (
        <video src={value} controls muted className="w-full rounded-lg bg-black" />
      ) : (
        <p className="text-[11px] font-semibold text-red-400" data-testid="editor-video-invalid">Couldn't detect a video — paste {allowFile ? 'a Wistia link or a direct .mp4 link' : "the embed code or video link from Wistia's Embed & Share screen"}.</p>
      ))}
    </>
  );
};

const VideoControl = ({ def, v, onChange, active }) => (
  <div id={`fld-${def.k}`} className={fieldBox(active)} data-testid={`editor-field-${def.k}`}>
    <label className={lbl}>{def.label}</label>
    <VideoInput value={v} onChange={onChange} testid={`editor-video-${def.k}`} />
    <p className="text-[10px] text-white/30">In Wistia: open your video → Embed &amp; Share → copy the inline embed code or the link. Leave empty for the placeholder.</p>
  </div>
);

const SelectControl = ({ def, v, onChange }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center justify-between" data-testid={`editor-field-${def.k}`}>
    <label className="text-xs font-semibold text-white/70">{def.label}</label>
    <select value={v || ''} onChange={(e) => onChange(e.target.value)} className={miniSel} data-testid={`editor-select-${def.k}`}>
      {(def.options || []).map(([val, l]) => <option key={val} value={val}>{l}</option>)}
    </select>
  </div>
);

const ListControl = ({ def, v = [], onChange, active }) => {
  const update = (i, k, val) => onChange(v.map((it, idx) => (idx === i ? { ...it, [k]: val } : it)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= v.length) return;
    const n = [...v];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div id={`fld-${def.k}`} className={fieldBox(active)} data-testid={`editor-field-${def.k}`}>
      <label className={lbl}>{def.label} ({v.length})</label>
      <div className="space-y-2">
        {v.map((item, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5 space-y-1.5">
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded text-white/35 hover:text-white disabled:opacity-25" data-testid={`editor-list-up-${def.k}-${i}`}><ArrowUp size={11} /></button>
              <button onClick={() => move(i, 1)} disabled={i === v.length - 1} className="p-1 rounded text-white/35 hover:text-white disabled:opacity-25" data-testid={`editor-list-down-${def.k}-${i}`}><ArrowDown size={11} /></button>
              <button onClick={() => onChange(v.filter((_, idx) => idx !== i))} className="p-1 rounded text-white/35 hover:text-red-400" data-testid={`editor-list-del-${def.k}-${i}`}><Trash2 size={11} /></button>
            </div>
            {(def.itemFields || []).map((f) => f.kind === 'textarea' ? (
              <textarea key={f.k} value={item[f.k] || ''} onChange={(e) => update(i, f.k, e.target.value)} rows={2} placeholder={f.label} className={`${inp} resize-y`} data-testid={`editor-list-${def.k}-${i}-${f.k}`} />
            ) : (
              <input key={f.k} value={item[f.k] || ''} onChange={(e) => update(i, f.k, e.target.value)} placeholder={f.label} className={inp} data-testid={`editor-list-${def.k}-${i}-${f.k}`} />
            ))}
          </div>
        ))}
      </div>
      <button
        onClick={() => onChange([...v, Object.fromEntries((def.itemFields || []).map((f) => [f.k, '']))])}
        className="w-full rounded-lg border border-dashed border-white/20 py-2 text-xs font-semibold text-white/50 hover:text-white hover:border-white/40 transition-colors"
        data-testid={`editor-list-add-${def.k}`}
      >
        <Plus size={12} className="inline mr-1 -mt-0.5" />Add item
      </button>
    </div>
  );
};

/* Comparison-table editor: columns (header/sub/width/highlight) + rows of cells */
const TableControl = ({ s, onProp, active, sections }) => {
  const cols = s.props.cols || [];
  const rows = s.props.rows || [];
  const setCols = (n) => onProp('cols', n);
  const setRows = (n) => onProp('rows', n);
  const setCol = (i, patch) => setCols(cols.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addCol = () => {
    setCols([...cols, { header: 'New column', sub: '', width: '', highlight: false }]);
    setRows(rows.map((r) => ({ ...r, cells: [...(r.cells || []), { kind: 'check' }] })));
  };
  const delCol = (i) => {
    if (cols.length <= 2) return;
    setCols(cols.filter((_, idx) => idx !== i));
    setRows(rows.map((r) => ({ ...r, cells: (r.cells || []).filter((_, idx) => idx !== i) })));
  };
  const setCell = (ri, ci, patch) => setRows(rows.map((r, idx) => (idx === ri ? { ...r, cells: r.cells.map((c2, cidx) => (cidx === ci ? { ...c2, ...patch } : c2)) } : r)));
  const setCellKind = (ri, ci, kind) => {
    const base = kind === 'text' ? { kind, t: '' } : kind === 'button' ? { kind, v: { label: 'Click here', bg: '#141414', fg: '#F0EFEC', fx: 'none' } } : { kind };
    setRows(rows.map((r, idx) => (idx === ri ? { ...r, cells: r.cells.map((c2, cidx) => (cidx === ci ? base : c2)) } : r)));
  };
  const moveRow = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const n = [...rows];
    [n[i], n[j]] = [n[j], n[i]];
    setRows(n);
  };
  const dupRow = (i) => {
    const n = [...rows];
    n.splice(i + 1, 0, JSON.parse(JSON.stringify(rows[i])));
    setRows(n);
  };
  const addRow = () => setRows([...rows, { cells: cols.map((_, ci) => (ci === 0 ? { kind: 'text', t: 'New feature' } : { kind: 'check' })) }]);
  return (
    <div id="fld-cols" className={fieldBox(active)} data-testid="editor-field-table">
      <label className={lbl}>Columns ({cols.length})</label>
      <div className="space-y-2">
        {cols.map((c, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input value={c.header || ''} onChange={(e) => setCol(i, { header: e.target.value })} placeholder="Header" className={inp} data-testid={`editor-tbl-col-header-${i}`} />
              <button
                onClick={() => setCol(i, { highlight: !c.highlight })}
                className={`p-1.5 rounded-lg border transition-colors shrink-0 ${c.highlight ? 'bg-amber-400 text-black border-amber-400' : 'border-white/10 text-white/40 hover:text-white'}`}
                title="Highlight this column"
                data-testid={`editor-tbl-col-hl-${i}`}
              ><Star size={12} /></button>
              <button onClick={() => delCol(i)} disabled={cols.length <= 2} className="p-1.5 rounded text-white/35 hover:text-red-400 disabled:opacity-25 shrink-0" title="Remove column" data-testid={`editor-tbl-col-del-${i}`}><Trash2 size={12} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <input value={c.sub || ''} onChange={(e) => setCol(i, { sub: e.target.value })} placeholder="Sub-line (optional)" className={`${inp} flex-1`} data-testid={`editor-tbl-col-sub-${i}`} />
              <select value={c.width || ''} onChange={(e) => setCol(i, { width: e.target.value })} className={miniSel} title="Column width" data-testid={`editor-tbl-col-width-${i}`}>
                {COL_WIDTHS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addCol} className="w-full rounded-lg border border-dashed border-white/20 py-2 text-xs font-semibold text-white/50 hover:text-white hover:border-white/40 transition-colors" data-testid="editor-tbl-add-col">
        <Plus size={12} className="inline mr-1 -mt-0.5" />Add column
      </button>

      <label className={`${lbl} pt-2`}>Rows ({rows.length})</label>
      <div className="space-y-2">
        {rows.map((r, ri) => (
          <div key={ri} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5 space-y-1.5">
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => moveRow(ri, -1)} disabled={ri === 0} className="p-1 rounded text-white/35 hover:text-white disabled:opacity-25" data-testid={`editor-tbl-row-up-${ri}`}><ArrowUp size={11} /></button>
              <button onClick={() => moveRow(ri, 1)} disabled={ri === rows.length - 1} className="p-1 rounded text-white/35 hover:text-white disabled:opacity-25" data-testid={`editor-tbl-row-down-${ri}`}><ArrowDown size={11} /></button>
              <button onClick={() => dupRow(ri)} className="p-1 rounded text-white/35 hover:text-cyan-400" title="Duplicate row" data-testid={`editor-tbl-row-dup-${ri}`}><Copy size={11} /></button>
              <button onClick={() => setRows(rows.filter((_, idx) => idx !== ri))} className="p-1 rounded text-white/35 hover:text-red-400" data-testid={`editor-tbl-row-del-${ri}`}><Trash2 size={11} /></button>
            </div>
            {cols.map((c, ci) => {
              const cell = (r.cells || [])[ci] || { kind: 'text', t: '' };
              return (
                <div key={ci} className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-white/35 w-14 truncate shrink-0" title={c.header}>{c.header || `Col ${ci + 1}`}</span>
                    <select value={cell.kind || 'text'} onChange={(e) => setCellKind(ri, ci, e.target.value)} className={miniSel} data-testid={`editor-tbl-cell-kind-${ri}-${ci}`}>
                      {CELL_KINDS.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                    </select>
                    {(cell.kind === 'text' || !cell.kind) && (
                      <input value={cell.t || ''} onChange={(e) => setCell(ri, ci, { t: e.target.value })} className={`${inp} flex-1`} placeholder="Cell text" data-testid={`editor-tbl-cell-text-${ri}-${ci}`} />
                    )}
                    {cell.kind === 'button' && (
                      <>
                        <input value={cell.v?.label || ''} onChange={(e) => setCell(ri, ci, { v: { ...(cell.v || {}), label: e.target.value } })} className={`${inp} flex-1`} placeholder="Button text" data-testid={`editor-tbl-cell-btnlabel-${ri}-${ci}`} />
                        <ColorDot value={cell.v?.bg} onChange={(c2) => setCell(ri, ci, { v: { ...(cell.v || {}), bg: c2 } })} title="Button color" />
                        <ColorDot value={cell.v?.fg} onChange={(c2) => setCell(ri, ci, { v: { ...(cell.v || {}), fg: c2 } })} title="Button text color" />
                      </>
                    )}
                  </div>
                  {cell.kind === 'button' && (
                    <div className="pl-14">
                      <CtaActionRow v={cell.v || {}} onChange={(patch) => setCell(ri, ci, { v: { ...(cell.v || {}), ...patch } })} sections={sections} k={`tbl-${ri}-${ci}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <button onClick={addRow} className="w-full rounded-lg border border-dashed border-white/20 py-2 text-xs font-semibold text-white/50 hover:text-white hover:border-white/40 transition-colors" data-testid="editor-tbl-add-row">
        <Plus size={12} className="inline mr-1 -mt-0.5" />Add row
      </button>
    </div>
  );
};

/* Side-by-side plan cards editor */
const PlansControl = ({ def, v = [], onChange, active, sections }) => {
  const setCard = (i, patch) => onChange(v.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const setCta = (i, patch) => setCard(i, { cta: { ...(v[i].cta || {}), ...patch } });
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= v.length) return; const n = [...v]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const dup = (i) => { const n = [...v]; n.splice(i + 1, 0, JSON.parse(JSON.stringify(v[i]))); onChange(n); };
  const add = () => onChange([...v, { badge: '', title: 'New plan', price: '$0', price_note: '', chip: '', features: ['Feature one', 'Feature two'], cta: { label: 'Choose this plan', bg: '#141414', fg: '#F0EFEC', fx: 'none' }, foot: '', bg: '#FFFFFF', fg: '#141414', highlight: false }]);
  return (
    <div id="fld-cards" className={fieldBox(active)} data-testid="editor-field-plans">
      <label className={lbl}>{def.label} ({v.length})</label>
      <p className="text-[10px] text-white/30 leading-snug">Click any text on a card in the preview to edit it. Toggle ★ for the glow + badge on the recommended plan.</p>
      <div className="space-y-2">
        {v.map((c, i) => (
          <div key={i} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5 space-y-2">
            <div className="flex items-center gap-1">
              <span className="text-[11px] font-bold text-white/70 flex-1 truncate">{snippet(c.title)}</span>
              <button
                onClick={() => setCard(i, { highlight: !c.highlight })}
                className={`p-1.5 rounded-lg border transition-colors shrink-0 ${c.highlight ? 'bg-amber-400 text-black border-amber-400' : 'border-white/10 text-white/40 hover:text-white'}`}
                title="Recommended — glow + badge"
                data-testid={`editor-plan-hl-${i}`}
              ><Star size={12} /></button>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded text-white/35 hover:text-white disabled:opacity-25" data-testid={`editor-plan-up-${i}`}><ArrowUp size={11} /></button>
              <button onClick={() => move(i, 1)} disabled={i === v.length - 1} className="p-1 rounded text-white/35 hover:text-white disabled:opacity-25" data-testid={`editor-plan-down-${i}`}><ArrowDown size={11} /></button>
              <button onClick={() => dup(i)} className="p-1 rounded text-white/35 hover:text-cyan-400" title="Duplicate card" data-testid={`editor-plan-dup-${i}`}><Copy size={11} /></button>
              <button onClick={() => onChange(v.filter((_, idx) => idx !== i))} className="p-1 rounded text-white/35 hover:text-red-400" data-testid={`editor-plan-del-${i}`}><Trash2 size={11} /></button>
            </div>
            {c.highlight && (
              <input value={c.badge || ''} onChange={(e) => setCard(i, { badge: e.target.value })} className={inp} placeholder="Badge (e.g. RECOMMENDED)" data-testid={`editor-plan-badge-${i}`} />
            )}
            <input value={c.chip || ''} onChange={(e) => setCard(i, { chip: e.target.value })} className={inp} placeholder="Yellow chip under price (e.g. Less than $16/day)" data-testid={`editor-plan-chip-${i}`} />
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Card <ColorDot value={c.bg} onChange={(c2) => setCard(i, { bg: c2 })} title="Card background" /></span>
              <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Text <ColorDot value={c.fg} onChange={(c2) => setCard(i, { fg: c2 })} title="Card text color" /></span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-white/35">Features — one per line</span>
              <textarea
                value={(c.features || []).join('\n')}
                onChange={(e) => setCard(i, { features: e.target.value.split('\n') })}
                rows={4}
                className={`${inp} resize-y mt-1`}
                data-testid={`editor-plan-features-${i}`}
              />
            </div>
            <input value={c.foot || ''} onChange={(e) => setCard(i, { foot: e.target.value })} className={inp} placeholder="Line under the button (e.g. No payment required)" data-testid={`editor-plan-foot-${i}`} />
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Button <ColorDot value={c.cta?.bg} onChange={(c2) => setCta(i, { bg: c2 })} title="Button color" /></span>
              <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Text <ColorDot value={c.cta?.fg} onChange={(c2) => setCta(i, { fg: c2 })} title="Button text color" /></span>
              <select value={c.cta?.fx || 'none'} onChange={(e) => setCta(i, { fx: e.target.value })} className={`${miniSel} ml-auto`} title="Attention effect">
                {FX.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
              </select>
            </div>
            <CtaActionRow v={c.cta || {}} onChange={(patch) => setCta(i, patch)} sections={sections} k={`plan-${i}`} />
          </div>
        ))}
      </div>
      <button onClick={add} className="w-full rounded-lg border border-dashed border-white/20 py-2 text-xs font-semibold text-white/50 hover:text-white hover:border-white/40 transition-colors" data-testid="editor-plan-add">
        <Plus size={12} className="inline mr-1 -mt-0.5" />Add plan card
      </button>
    </div>
  );
};

/* Image slider rows editor — upload or paste URLs for top/bottom marquee rows */
const ImagesControl = ({ s, onProp, active }) => {
  const [busy, setBusy] = useState(null);
  const [urls, setUrls] = useState({ images_top: '', images_bottom: '' });
  const rows = [['images_top', 'Top row — slides left'], ['images_bottom', 'Bottom row — slides right (optional)']];
  const upload = async (key, files) => {
    if (!files.length) return;
    setBusy(key);
    let list = [...(s.props[key] || [])];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        // eslint-disable-next-line no-await-in-loop
        const r = await axios.post(`${API}/api/funnel/admin/images`, fd, { headers: auth() });
        list = [...list, r.data.url];
        onProp(key, list);
      }
      toast.success(`${files.length} image${files.length === 1 ? '' : 's'} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(null);
    }
  };
  const move = (key, i, d) => {
    const list = [...(s.props[key] || [])];
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    onProp(key, list);
  };
  return (
    <div id="fld-images_top" className={fieldBox(active)} data-testid="editor-field-images">
      {rows.map(([key, label]) => {
        const list = s.props[key] || [];
        return (
          <div key={key} className="space-y-2">
            <label className={lbl}>{label} ({list.length})</label>
            {list.length > 0 && (
              <div className="grid grid-cols-3 gap-1.5">
                {list.map((src, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-[16/10] bg-black/40">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-0.5">
                      <button onClick={() => move(key, i, -1)} disabled={i === 0} className="p-1 text-white/70 hover:text-white disabled:opacity-30" title="Move earlier"><ChevronLeft size={12} /></button>
                      <button onClick={() => onProp(key, list.filter((_, idx) => idx !== i))} className="p-1 text-white/70 hover:text-red-400" title="Remove image" data-testid={`editor-img-del-${key}-${i}`}><Trash2 size={12} /></button>
                      <button onClick={() => move(key, i, 1)} disabled={i === list.length - 1} className="p-1 text-white/70 hover:text-white disabled:opacity-30" title="Move later"><ChevronRight size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <label className={`block rounded-lg border border-dashed border-white/20 py-2 text-[11px] font-semibold text-center cursor-pointer transition-colors ${busy === key ? 'text-cyan-400 border-cyan-400/40' : 'text-white/50 hover:text-white hover:border-cyan-400/50'}`}>
              {busy === key ? 'Uploading…' : '⬆ Upload images'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                disabled={busy === key}
                onChange={(e) => { upload(key, Array.from(e.target.files || [])); e.target.value = ''; }}
                data-testid={`editor-img-upload-${key}`}
              />
            </label>
            <input
              value={urls[key]}
              onChange={(e) => setUrls((u) => ({ ...u, [key]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && urls[key].trim()) {
                  onProp(key, [...list, urls[key].trim()]);
                  setUrls((u) => ({ ...u, [key]: '' }));
                }
              }}
              className={inp}
              placeholder="…or paste an image URL and press Enter"
              data-testid={`editor-img-url-${key}`}
            />
          </div>
        );
      })}
      <p className="text-[10px] text-white/30 leading-snug">PNG, JPG, WEBP or GIF up to 8MB. Landscape images look best — rows loop automatically and pause on hover.</p>
    </div>
  );
};

/* Floating reel video settings (page design panel) */
const ReelControl = ({ v = {}, onChange, active, sections }) => {
  const cta = v.cta || {};
  const setCta = (patch) => onChange({ cta: { ...cta, ...patch } });
  return (
    <div id="fld-reel" className={fieldBox(active)} data-testid="editor-field-reel">
      <div className="flex items-center justify-between gap-2">
        <label className={lbl}>Floating reel video</label>
        <MiniToggle on={!!v.enabled} onToggle={() => onChange({ enabled: !v.enabled })} testid="editor-reel-enabled" />
      </div>
      <p className="text-[10px] text-white/30 leading-snug">Desktop only — a vertical video pinned bottom-right that follows the scroll. Plays muted while visitors browse; clicking it restarts with sound.</p>
      {v.enabled && (
        <>
          <VideoInput value={v.video_url} onChange={(nv) => onChange({ video_url: nv })} testid="editor-reel-video" allowFile />
          <div>
            <label className={lbl}>Unmute overlay text</label>
            <input value={v.overlay ?? 'Click here to unmute'} onChange={(e) => onChange({ overlay: e.target.value })} className={`${inp} mt-1`} data-testid="editor-reel-overlay" />
          </div>
          <div>
            <label className={lbl}>CTA button text</label>
            <input value={cta.label || ''} onChange={(e) => setCta({ label: e.target.value })} className={`${inp} mt-1`} placeholder="Save my free seat" data-testid="editor-reel-cta-label" />
          </div>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Button <ColorDot value={cta.bg} onChange={(c) => setCta({ bg: c })} testid="editor-reel-cta-bg" title="Button color" /></span>
            <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Text <ColorDot value={cta.fg} onChange={(c) => setCta({ fg: c })} testid="editor-reel-cta-fg" title="Button text color" /></span>
          </div>
          <CtaActionRow v={cta} onChange={setCta} sections={sections} k="reel" />
        </>
      )}
    </div>
  );
};

/* Social-proof registration popups settings (page design panel) */
const ProofControl = ({ v = {}, onChange }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2.5" data-testid="editor-field-proof">
    <div className="flex items-center justify-between gap-2">
      <label className={lbl}>Registration popups</label>
      <MiniToggle on={!!v.enabled} onToggle={() => onChange({ enabled: !v.enabled })} testid="editor-proof-enabled" />
    </div>
    <p className="text-[10px] text-white/30 leading-snug">Bottom-left "just registered" cards — real registrants shown as social proof while visitors browse.</p>
    {v.enabled && (
      <>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-white/45">Show a registrant every</span>
          <span className="flex items-center gap-1.5">
            <input type="number" min={5} value={v.every_s ?? 12} onChange={(e) => onChange({ every_s: parseInt(e.target.value, 10) || 12 })} className={`${miniSel} w-16 text-center`} data-testid="editor-proof-every" />
            <span className="text-[11px] text-white/40">sec</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-white/45">Ghost registrants (realistic filler)</span>
          <MiniToggle on={!!v.ghosts} onToggle={() => onChange({ ghosts: !v.ghosts })} testid="editor-proof-ghosts" />
        </div>
        {v.ghosts && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-white/45">Show a ghost every</span>
            <span className="flex items-center gap-1.5">
              <input type="number" min={5} value={v.ghost_every_s ?? 25} onChange={(e) => onChange({ ghost_every_s: parseInt(e.target.value, 10) || 25 })} className={`${miniSel} w-16 text-center`} data-testid="editor-proof-ghost-every" />
              <span className="text-[11px] text-white/40">sec</span>
            </span>
          </div>
        )}
      </>
    )}
  </div>
);

export default function FunnelEditorPage() {
  const [pages, setPages] = useState(() => mergeAll({}));
  const [page, setPage] = useState('lander');
  const [device, setDevice] = useState('desktop');
  const [sel, setSel] = useState(null); // {sec, k}
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const [dims, setDims] = useState({ w: 1000, h: 700 });
  const [dragX, setDragX] = useState(null); // extras drag index (left panel)
  const frameRef = useRef(null);
  const wrapRef = useRef(null);
  const pageRef = useRef(page);
  pageRef.current = page;

  const sections = pages[page] || [];
  const pageSettings = sections.find((s) => s.type === 'page_settings');
  const listSections = sections.filter((s) => s.type !== 'page_settings');
  const jobs = ((pages.thanks || []).find((s) => s.type === 'three_jobs') || {}).props || {};

  useEffect(() => {
    axios.get(`${API}/api/funnel/content`, { headers: auth() })
      .then((r) => setPages(mergeAll((r.data && r.data.pages) || {})))
      .catch(() => toast.error('Failed to load page content'));
  }, []);

  const post = useCallback((msg) => {
    try { frameRef.current?.contentWindow?.postMessage({ vx: 'funnel-editor', ...msg }, '*'); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.vx !== 'funnel-editor-preview') return;
      if (d.type === 'ready') setFrameReady(true);
      if (d.type === 'select') setSel({ sec: d.sec, k: d.k || null });
      if (d.type === 'inline') {
        const p = pageRef.current;
        setPages((prev) => ({
          ...prev,
          [p]: prev[p].map((s) => {
            if (s.id !== d.sec) return s;
            const cur = getDeep(s.props, d.path);
            const val = (cur && typeof cur === 'object' && !Array.isArray(cur)) ? { ...cur, t: d.t } : d.t;
            return { ...s, props: setDeep(s.props, d.path, val) };
          }),
        }));
        setDirty((dd) => ({ ...dd, [p]: true }));
      }
      if (d.type === 'hide') {
        const p = pageRef.current;
        setPages((prev) => ({
          ...prev,
          [p]: prev[p].map((s) => {
            if (s.id !== d.sec) return s;
            const m = String(d.path || '').match(/^extras\.(\d+)\.v$/);
            if (m) {
              const extras = (s.props.extras || []).filter((_, idx) => idx !== +m[1]);
              return { ...s, props: { ...s.props, extras } };
            }
            const cur = getDeep(s.props, d.path);
            const val = (cur && typeof cur === 'object' && !Array.isArray(cur)) ? { ...cur, hidden: true } : '';
            return { ...s, props: setDeep(s.props, d.path, val) };
          }),
        }));
        setDirty((dd) => ({ ...dd, [p]: true }));
        toast.success('Removed — bring it back with the eye icon in the left panel');
        setSel(null);
      }
      if (d.type === 'extraDrop') {
        if (d.beforeId && d.beforeId === d.fromId) return;
        const p = pageRef.current;
        setPages((prev) => {
          const list = prev[p] || [];
          let moved = null;
          let out = list.map((s2) => {
            if (s2.id !== d.fromSec) return s2;
            const extras = [...(s2.props.extras || [])];
            const idx = extras.findIndex((x) => x.id === d.fromId);
            if (idx >= 0) moved = extras.splice(idx, 1)[0];
            return { ...s2, props: { ...s2.props, extras } };
          });
          if (!moved) return prev;
          const m = { ...moved, at: d.at || 'end' };
          out = out.map((s2) => {
            if (s2.id !== d.toSec) return s2;
            const extras = [...(s2.props.extras || [])];
            const bi = d.beforeId ? extras.findIndex((x) => x.id === d.beforeId) : -1;
            if (bi >= 0) extras.splice(bi, 0, m); else extras.push(m);
            return { ...s2, props: { ...s2.props, extras } };
          });
          return { ...prev, [p]: out };
        });
        setDirty((dd) => ({ ...dd, [p]: true }));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  useEffect(() => { setFrameReady(false); setSel(null); setAddOpen(false); }, [page, device]);
  useEffect(() => { if (frameReady) post({ type: 'content', sections: pages[page], jobs }); }, [frameReady, pages, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (frameReady) post({ type: 'select', id: sel ? `${sel.sec}:${sel.k ?? ''}` : null }); }, [sel, frameReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sel && sel.k) {
      const base = sel.k.split('.')[0];
      setTimeout(() => document.getElementById(`fld-${base}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
    }
  }, [sel]);

  useEffect(() => {
    const calc = () => { const el = wrapRef.current; if (el) setDims({ w: el.clientWidth, h: el.clientHeight }); };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [device]);

  useEffect(() => {
    const warn = (e) => { if (Object.values(dirty).some(Boolean)) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const updateSections = (fn, pg = page) => {
    setPages((prev) => ({ ...prev, [pg]: fn(prev[pg]) }));
    setDirty((d) => ({ ...d, [pg]: true }));
  };
  const updateProp = (secId, key, val) => updateSections((list) => list.map((s) => (s.id === secId ? { ...s, props: { ...s.props, [key]: val } } : s)));
  const moveSec = (i, d) => updateSections((list) => {
    const rest = list.filter((s) => s.type !== 'page_settings');
    const pinned = list.filter((s) => s.type === 'page_settings');
    const j = i + d;
    if (j < 0 || j >= rest.length) return list;
    const n = [...rest];
    [n[i], n[j]] = [n[j], n[i]];
    return [...pinned, ...n];
  });
  const toggleSec = (id) => updateSections((list) => list.map((s) => (s.id === id ? { ...s, enabled: s.enabled === false } : s)));
  const removeSec = (id) => {
    if (!window.confirm('Remove this section? You can restore everything with "Reset page".')) return;
    updateSections((list) => list.filter((s) => s.id !== id));
    if (sel?.sec === id) setSel(null);
  };
  const addBlock = (type) => {
    const b = newBlock(type);
    updateSections((list) => [...list, b]);
    setAddOpen(false);
    setSel({ sec: b.id, k: null });
    setTimeout(() => post({ type: 'scroll', id: b.id }), 250);
  };
  const selectSec = (id) => {
    setSel({ sec: id, k: null });
    post({ type: 'scroll', id });
  };

  /* ── extras ── */
  const addExtra = (secId, type, at = 'end', v = null) => {
    const x = newExtra(type);
    if (at) x.at = at;
    if (v) x.v = JSON.parse(JSON.stringify(v));
    updateSections((list) => list.map((s) => (s.id === secId ? { ...s, props: { ...s.props, extras: [...(s.props.extras || []), x] } } : s)));
    setTimeout(() => post({ type: 'scroll', id: secId }), 200);
  };
  const updateExtra = (secId, i, patch) => updateSections((list) => list.map((s) => {
    if (s.id !== secId) return s;
    const extras = (s.props.extras || []).map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    return { ...s, props: { ...s.props, extras } };
  }));
  const removeExtra = (secId, i) => updateSections((list) => list.map((s) => (s.id === secId ? { ...s, props: { ...s.props, extras: (s.props.extras || []).filter((_, idx) => idx !== i) } } : s)));
  const duplicateExtra = (secId, i) => updateSections((list) => list.map((s) => {
    if (s.id !== secId) return s;
    const extras = [...(s.props.extras || [])];
    const copy = { ...JSON.parse(JSON.stringify(extras[i])), id: `x_${Math.random().toString(36).slice(2, 9)}` };
    extras.splice(i + 1, 0, copy);
    return { ...s, props: { ...s.props, extras } };
  }));
  const reorderExtras = (secId, from, to) => {
    if (from == null || to == null || from === to) return;
    updateSections((list) => list.map((s) => {
      if (s.id !== secId) return s;
      const extras = [...(s.props.extras || [])];
      if (!extras[from] || !extras[to]) return s;
      const adoptAt = extras[to].at || 'end';
      const [m] = extras.splice(from, 1);
      extras.splice(to, 0, { ...m, at: adoptAt });
      return { ...s, props: { ...s.props, extras } };
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/funnel/admin/content`, { page, sections: pages[page] }, { headers: auth() });
      setDirty((d) => ({ ...d, [page]: false }));
      toast.success('Page published — changes are live');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publish failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset this page to the original design? All your edits on this page will be lost.')) return;
    setResetting(true);
    try {
      await axios.delete(`${API}/api/funnel/admin/content/${page}`, { headers: auth() });
      setPages((prev) => ({ ...prev, [page]: mergeAll({})[page] }));
      setDirty((d) => ({ ...d, [page]: false }));
      toast.success('Page reset to the original design');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const applyBgToAllPages = async () => {
    const bg = pageSettings?.props?.bg || '';
    setPages((prev) => {
      const out = { ...prev };
      Object.keys(out).forEach((pg) => {
        out[pg] = out[pg].map((s) => (s.type === 'page_settings' ? { ...s, props: { ...s.props, bg } } : s));
      });
      return out;
    });
    setDirty({ lander: true, kit: true, thanks: true, vip: true });
    if (window.confirm('Background applied to all 4 pages. Publish all pages now to make it live?')) {
      setSaving(true);
      try {
        // read latest state after the update above
        const latest = { ...pages };
        Object.keys(latest).forEach((pg) => {
          latest[pg] = latest[pg].map((s) => (s.type === 'page_settings' ? { ...s, props: { ...s.props, bg } } : s));
        });
        for (const pg of ['lander', 'kit', 'thanks', 'vip']) {
          // eslint-disable-next-line no-await-in-loop
          await axios.put(`${API}/api/funnel/admin/content`, { page: pg, sections: latest[pg] }, { headers: auth() });
        }
        setDirty({});
        toast.success('All 4 pages published');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Publish failed — some pages may not be saved');
      } finally {
        setSaving(false);
      }
    }
  };

  const scale = Math.min(1, dims.w / 1440);
  const selSection = sel ? sections.find((s) => s.id === sel.sec) : null;
  const livePath = PAGE_TABS.find((t) => t.id === page)?.path || '/challenge';

  const renderExtras = (s) => {
    const extras = s.props.extras || [];
    return (
      <div className="pt-1" data-testid="editor-extras-panel">
        <div className="flex items-center justify-between mb-2">
          <span className={lbl}>Added elements ({extras.length})</span>
        </div>
        {extras.length > 0 && (
          <p className="text-[10px] text-white/30 mb-2 leading-snug">Drag the ⠿ grip here — or drag the element itself in the preview — to move it anywhere.</p>
        )}
        <div className="space-y-2">
          {extras.map((x, i) => {
            const active = sel?.k === `extras.${i}.v` || (sel?.k || '').startsWith(`extras.${i}.v.`);
            const TypeIcon = (EXTRA_TYPES.find(([t]) => t === x.type) || [null, null, Type])[2];
            return (
              <div
                key={x.id}
                className={fieldBox(active)}
                data-testid={`editor-extra-${x.id}`}
                onDragOver={(e) => { if (dragX != null) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); reorderExtras(s.id, dragX, i); setDragX(null); }}
              >
                <div className="flex items-center gap-2">
                  <span
                    draggable
                    onDragStart={() => setDragX(i)}
                    onDragEnd={() => setDragX(null)}
                    className="cursor-grab text-white/30 hover:text-white/70 transition-colors shrink-0"
                    title="Drag to reorder"
                    data-testid={`editor-extra-grip-${x.id}`}
                  ><GripVertical size={13} /></span>
                  <TypeIcon size={12} className="text-cyan-400/80 shrink-0" />
                  <span className="text-[11px] font-bold text-white/70 capitalize flex-1">{x.type}</span>
                  <button onClick={() => duplicateExtra(s.id, i)} className="p-1 rounded text-white/35 hover:text-cyan-400" title="Duplicate" data-testid={`editor-extra-dup-${x.id}`}><Copy size={12} /></button>
                  <button onClick={() => removeExtra(s.id, i)} className="p-1 rounded text-white/35 hover:text-red-400" title="Remove" data-testid={`editor-extra-del-${x.id}`}><Trash2 size={12} /></button>
                </div>
                {x.type === 'text' && (
                  <>
                    <button onClick={() => { setSel({ sec: s.id, k: `extras.${i}.v` }); post({ type: 'scroll', id: s.id }); }} className="text-left w-full group" data-testid={`editor-extra-jump-${x.id}`}>
                      <span className="block text-[11px] text-white/40 group-hover:text-white/60 leading-snug truncate transition-colors">{snippet(x.v?.t)}</span>
                    </button>
                    <StyleRow v={x.v || {}} onChange={(patch) => updateExtra(s.id, i, { v: { ...(x.v || {}), ...patch } })} k={`x-${x.id}`} />
                  </>
                )}
                {x.type === 'button' && (
                  <>
                    <div className="flex items-center gap-4">
                      <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Button <ColorDot value={x.v?.bg} onChange={(c) => updateExtra(s.id, i, { v: { ...(x.v || {}), bg: c } })} title="Button color" /></span>
                      <span className="inline-flex items-center gap-2 text-[11px] text-white/45">Text <ColorDot value={x.v?.fg} onChange={(c) => updateExtra(s.id, i, { v: { ...(x.v || {}), fg: c } })} title="Text color" /></span>
                      <select value={x.v?.fx || 'nudge'} onChange={(e) => updateExtra(s.id, i, { v: { ...(x.v || {}), fx: e.target.value } })} className={`${miniSel} ml-auto`} title="Attention effect">
                        {FX.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                      </select>
                    </div>
                    <CtaActionRow v={x.v || {}} onChange={(patch) => updateExtra(s.id, i, { v: { ...(x.v || {}), ...patch } })} sections={listSections} k={`x-${x.id}`} />
                  </>
                )}
                {x.type === 'video' && (
                  <VideoInput value={typeof x.v === 'string' ? x.v : ''} onChange={(nv) => updateExtra(s.id, i, { v: nv })} testid={`editor-extra-video-${x.id}`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-1.5 mt-2">
          {EXTRA_TYPES.map(([t, l, Icon]) => (
            <button key={t} onClick={() => addExtra(s.id, t)} className="flex-1 rounded-lg border border-dashed border-white/20 py-2 text-[11px] font-semibold text-white/50 hover:text-white hover:border-cyan-400/50 transition-colors flex items-center justify-center gap-1" data-testid={`editor-add-extra-${t}`}>
              <Icon size={11} /> {l}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderSectionList = () => (
    <>
      <div className="px-4 py-3.5 border-b border-white/10">
        <h2 className="text-sm font-black">Page sections</h2>
        <p className="text-[11px] text-white/40 mt-0.5 leading-snug">Click any element in the preview to edit it directly — or pick a section here.</p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {pageSettings && (
          <div
            onClick={() => setSel({ sec: pageSettings.id, k: null })}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${sel?.sec === pageSettings.id ? 'border-cyan-400/70 bg-cyan-400/[0.07]' : 'border-white/[0.08] bg-white/[0.05] hover:border-white/25'}`}
            data-testid="editor-page-design-row"
          >
            <Paintbrush size={13} className="text-cyan-400/80" />
            <span className="text-[13px] font-bold flex-1">Page design</span>
            <span className="w-4 h-4 rounded border border-white/20" style={{ background: pageSettings.props?.bg || '#F0EFEC' }} />
            <ChevronRight size={13} className="text-white/25 shrink-0" />
          </div>
        )}
        {listSections.map((s, i) => {
          const meta = SECTION_META[s.type] || { name: s.type };
          const hidden = s.enabled === false;
          return (
            <div
              key={s.id}
              onClick={() => selectSec(s.id)}
              className={`group flex items-center gap-1.5 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${sel?.sec === s.id ? 'border-cyan-400/70 bg-cyan-400/[0.07]' : 'border-white/[0.08] bg-white/[0.03] hover:border-white/25'} ${hidden ? 'opacity-40' : ''}`}
              data-testid={`editor-section-row-${s.id}`}
            >
              <span className="text-[13px] font-bold flex-1 truncate">{meta.name}</span>
              {meta.locked && <Lock size={11} className="text-white/25 shrink-0" />}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => { e.stopPropagation(); moveSec(i, -1); }} disabled={i === 0} className="p-1 rounded text-white/40 hover:text-white disabled:opacity-25" title="Move up" data-testid={`editor-section-up-${s.id}`}><ArrowUp size={13} /></button>
                <button onClick={(e) => { e.stopPropagation(); moveSec(i, 1); }} disabled={i === listSections.length - 1} className="p-1 rounded text-white/40 hover:text-white disabled:opacity-25" title="Move down" data-testid={`editor-section-down-${s.id}`}><ArrowDown size={13} /></button>
                {!meta.locked && (
                  <button onClick={(e) => { e.stopPropagation(); toggleSec(s.id); }} className="p-1 rounded text-white/40 hover:text-white" title={hidden ? 'Show section' : 'Hide section'} data-testid={`editor-section-toggle-${s.id}`}>
                    {hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                )}
                {!meta.locked && (
                  <button onClick={(e) => { e.stopPropagation(); removeSec(s.id); }} className="p-1 rounded text-white/40 hover:text-red-400" title="Remove section" data-testid={`editor-section-delete-${s.id}`}><Trash2 size={13} /></button>
                )}
              </div>
              <ChevronRight size={13} className="text-white/25 shrink-0" />
            </div>
          );
        })}
        <div className="pt-2">
          <button onClick={() => setAddOpen((v) => !v)} className="w-full rounded-xl border border-dashed border-white/20 py-3 text-xs font-bold text-white/50 hover:text-white hover:border-cyan-400/50 transition-colors" data-testid="editor-add-block-btn">
            <Plus size={13} className="inline mr-1 -mt-0.5" /> Add section
          </button>
          {addOpen && (
            <div className="mt-2 rounded-xl border border-white/10 bg-[#141416] overflow-hidden" data-testid="editor-add-block-menu">
              {BLOCK_LIBRARY.map((t) => (
                <button key={t} onClick={() => addBlock(t)} className="w-full text-left px-4 py-2.5 text-xs font-semibold text-white/70 hover:bg-white/[0.06] hover:text-white transition-colors" data-testid={`editor-add-${t}`}>
                  {(SECTION_META[t] || {}).name || t}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderFieldPanel = () => {
    const s = selSection;
    const meta = SECTION_META[s.type] || { name: s.type };
    const defs = FIELD_DEFS[s.type] || [];
    const isPageDesign = s.type === 'page_settings';
    return (
      <>
        <div className="px-4 py-3.5 border-b border-white/10 flex items-start gap-2.5">
          <button onClick={() => setSel(null)} className="p-1.5 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors shrink-0" data-testid="editor-back-to-sections">
            <ChevronLeft size={14} />
          </button>
          <div className="min-w-0">
            <h2 className="text-sm font-black truncate">{meta.name}</h2>
            {meta.note && <p className="text-[10px] text-white/35 mt-0.5 leading-snug">{meta.note}</p>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="editor-field-panel">
          {defs.map((def) => {
            const val = s.props ? s.props[def.k] : undefined;
            const active = sel.k === def.k || (sel.k || '').startsWith(`${def.k}.`);
            const jump = () => { setSel({ sec: s.id, k: def.k }); post({ type: 'scroll', id: s.id }); };
            switch (def.kind) {
              case 'text':
                return (
                  <TextControl
                    key={def.k} def={def} v={val} active={active} onJump={jump}
                    onChange={(patch) => updateProp(s.id, def.k, { ...(typeof val === 'object' && val ? val : { t: val || '' }), ...patch })}
                    onDuplicate={() => { addExtra(s.id, 'text', (EXTRA_ANCHORS[s.type] || []).includes(def.k) ? def.k : 'end', typeof val === 'object' && val ? val : { t: val || '' }); toast.success('Text duplicated — it appears right below the original'); }}
                  />
                );
              case 'cta':
                return <CtaControl key={def.k} def={def} v={val} onChange={(patch) => updateProp(s.id, def.k, { ...(val || {}), ...patch })} active={active} onJump={jump} sections={listSections} />;
              case 'color':
                return <ColorControl key={def.k} def={def} v={val} onChange={(c) => updateProp(s.id, def.k, c || def.def || null)} active={active} />;
              case 'toggle':
                return <ToggleControl key={def.k} def={def} v={val} onChange={(nv) => updateProp(s.id, def.k, nv)} />;
              case 'video':
                return <VideoControl key={def.k} def={def} v={val} onChange={(nv) => updateProp(s.id, def.k, nv)} active={active} />;
              case 'select':
                return <SelectControl key={def.k} def={def} v={val} onChange={(nv) => updateProp(s.id, def.k, nv)} />;
              case 'list':
                return <ListControl key={def.k} def={def} v={val || []} onChange={(nv) => updateProp(s.id, def.k, nv)} active={active} />;
              case 'table':
                return <TableControl key={def.k} s={s} onProp={(k2, v2) => updateProp(s.id, k2, v2)} active={!!(sel.k && (String(sel.k).startsWith('cols') || String(sel.k).startsWith('rows')))} sections={listSections} />;
              case 'plans':
                return <PlansControl key={def.k} def={def} v={val || []} onChange={(nv) => updateProp(s.id, def.k, nv)} active={!!(sel.k && String(sel.k).startsWith('cards'))} sections={listSections} />;
              case 'images':
                return <ImagesControl key={def.k} s={s} onProp={(k2, v2) => updateProp(s.id, k2, v2)} active={sel.k === 'images_top' || sel.k === 'images_bottom'} />;
              default:
                return null;
            }
          })}
          {!isPageDesign && s.type !== 'spacer' && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-center justify-between" data-testid="editor-field-cw">
              <label className="text-xs font-semibold text-white/70">Section width</label>
              <select value={s.props?.cw || ''} onChange={(e) => updateProp(s.id, 'cw', e.target.value || null)} className={miniSel} data-testid="editor-cw-select">
                <option value="">Normal</option>
                <option value="wide">Wide</option>
                <option value="full">Full width</option>
              </select>
            </div>
          )}
          {isPageDesign && (
            <>
              <button onClick={applyBgToAllPages} className="w-full rounded-xl border border-cyan-400/40 bg-cyan-400/10 py-2.5 text-xs font-bold text-cyan-300 hover:bg-cyan-400/20 transition-colors" data-testid="editor-apply-bg-all">
                Apply this background to all 4 pages
              </button>
              <p className="text-[10px] text-white/30 leading-snug">A section's own background color overrides the page color for that section.</p>
              <ReelControl
                v={pageSettings?.props?.reel || {}}
                onChange={(patch) => updateProp(s.id, 'reel', { ...(pageSettings?.props?.reel || {}), ...patch })}
                active={sel.k === 'reel'}
                sections={listSections}
              />
              <ProofControl
                v={pageSettings?.props?.proof || {}}
                onChange={(patch) => updateProp(s.id, 'proof', { ...(pageSettings?.props?.proof || {}), ...patch })}
              />
            </>
          )}
          {!isPageDesign && s.type !== 'spacer' && renderExtras(s)}
        </div>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0C0C0D] text-white flex flex-col" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }} data-testid="funnel-editor-page">
      {/* keep toasts from blocking top-bar controls while visible */}
      <style>{'[data-sonner-toaster]{pointer-events:none !important}'}</style>
      {/* top bar */}
      <div className="h-14 shrink-0 border-b border-white/10 flex items-center gap-3 px-4">
        <Link to="/admin/funnel" className="flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-white transition-colors" data-testid="editor-back-link">
          <ArrowLeft size={15} /> <span className="hidden sm:inline">Challenge Funnel</span>
        </Link>
        <span className="w-px h-5 bg-white/10" />
        <div className="flex gap-1 p-1 rounded-lg bg-white/[0.05]">
          {PAGE_TABS.map((t) => (
            <button key={t.id} onClick={() => setPage(t.id)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${page === t.id ? 'bg-white text-black' : 'text-white/50 hover:text-white'}`} data-testid={`editor-page-tab-${t.id}`}>
              {t.name}{dirty[t.id] && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" />}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex gap-1 p-1 rounded-lg bg-white/[0.05]">
            <button onClick={() => setDevice('desktop')} className={`p-1.5 rounded-md transition-colors ${device === 'desktop' ? 'bg-white text-black' : 'text-white/50 hover:text-white'}`} title="Desktop preview" data-testid="editor-device-desktop"><Monitor size={14} /></button>
            <button onClick={() => setDevice('mobile')} className={`p-1.5 rounded-md transition-colors ${device === 'mobile' ? 'bg-white text-black' : 'text-white/50 hover:text-white'}`} title="Mobile preview" data-testid="editor-device-mobile"><Smartphone size={14} /></button>
          </div>
          <a href={livePath} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-xs font-semibold text-white/60 hover:text-white hover:border-white/30 transition-colors" title="Open the live page" data-testid="editor-view-live">
            <ExternalLink size={13} /> <span className="hidden md:inline">Live page</span>
          </a>
          <button onClick={reset} disabled={resetting} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-xs font-semibold text-white/60 hover:text-red-400 hover:border-red-400/40 transition-colors disabled:opacity-50" data-testid="editor-reset-btn">
            {resetting ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} <span className="hidden md:inline">Reset page</span>
          </button>
          <button onClick={save} disabled={saving || !dirty[page]} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black transition-colors ${dirty[page] ? 'bg-cyan-400 text-black hover:bg-cyan-300' : 'bg-white/10 text-white/40'} disabled:cursor-not-allowed`} data-testid="editor-save-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? 'Publishing…' : dirty[page] ? 'Publish changes' : 'Published'}
          </button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* LEFT — 30% controls */}
        <div className="w-[30%] min-w-[320px] max-w-[460px] border-r border-white/10 flex flex-col bg-[#101012]" data-testid="editor-left-panel">
          {selSection ? renderFieldPanel() : renderSectionList()}
        </div>

        {/* RIGHT — 70% live preview */}
        <div ref={wrapRef} className="flex-1 bg-[#17181A] overflow-hidden relative" data-testid="editor-preview-wrap">
          {!frameReady && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              <Loader2 size={22} className="animate-spin text-white/30" />
            </div>
          )}
          {device === 'desktop' ? (
            <iframe
              key={`${page}-d`}
              ref={frameRef}
              src={`/funnel-editor-preview?page=${page}`}
              title="Funnel preview"
              style={{ width: 1440, height: dims.h / scale, transform: `scale(${scale})`, transformOrigin: 'top left', border: 0, background: '#F0EFEC' }}
              data-testid="editor-preview-iframe"
            />
          ) : (
            <div className="h-full flex items-start justify-center py-5 overflow-hidden">
              <div className="rounded-[2.2rem] border-[6px] border-black shadow-2xl overflow-hidden bg-[#F0EFEC]" style={{ width: 402, height: 'calc(100% - 8px)', maxHeight: 850 }}>
                <iframe
                  key={`${page}-m`}
                  ref={frameRef}
                  src={`/funnel-editor-preview?page=${page}`}
                  title="Funnel preview"
                  style={{ width: 390, height: '100%', border: 0, background: '#F0EFEC' }}
                  data-testid="editor-preview-iframe"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
